
import React, { useState, useRef } from 'react';
import { Lead, JobStatus } from '../types';
import { extractBusinessCardData } from '../services/gemini';
import MapComponent from './MapComponent';

interface Props {
  leads: Lead[];
  onConvert: (id: string) => void;
  onAddLead: (lead: Lead) => void;
  onUpdateLead?: (lead: Lead) => void;
  onDeleteLead?: (id: string) => Promise<{ ok: boolean; error?: string } | void> | void;
  /** Step 1 of the workflow: book a site visit to assess the tree and build the estimate. */
  onScheduleEstimate?: (lead: Lead, schedule: { date: string; time: string; durationMins: number; notes: string }) => void;
}

const LeadsView: React.FC<Props> = ({ leads, onConvert, onAddLead, onUpdateLead, onDeleteLead, onScheduleEstimate }) => {
  const [showForm, setShowForm] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [confirmingLeadId, setConfirmingLeadId] = useState<string | null>(null);
  const [selectedLeadForAttachments, setSelectedLeadForAttachments] = useState<Lead | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [estimateFor, setEstimateFor] = useState<Lead | null>(null);
  const [estimateForm, setEstimateForm] = useState({ date: new Date().toISOString().slice(0, 10), time: '09:00', durationMins: 60, notes: '' });

  // Requests awaiting action: enquiries still at LEAD, i.e. not yet scheduled for an estimate
  // or converted to a job. Surfaced at the top of the view so nothing sits unseen.
  const pendingRequests = leads.filter(l => !(l as any).isDeleted && l.status === JobStatus.LEAD);
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDeleteLead = async () => {
    if (!leadToDelete || !onDeleteLead || isDeleting) return;
    setIsDeleting(true);
    setDeleteError('');
    try {
      const result = await onDeleteLead(leadToDelete.id);
      if (result && result.ok === false) {
        setDeleteError(result.error || 'Delete failed. Please try again.');
        return;
      }
      setLeadToDelete(null);
    } catch (e: any) {
      setDeleteError(e?.message || 'Delete failed. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };
  const cardInputRef = useRef<HTMLInputElement>(null);
  
  const [newLead, setNewLead] = useState({
    clientName: '',
    email: '',
    phone: '',
    address: '',
    serviceType: 'Pruning' as any,
    attachments: [] as string[]
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const lead: Lead = {
      ...newLead,
      id: Math.random().toString(36).substr(2, 9),
      status: JobStatus.LEAD,
      createdAt: new Date().toISOString()
    };
    onAddLead(lead);
    setShowForm(false);
    setNewLead({ clientName: '', email: '', phone: '', address: '', serviceType: 'Pruning', attachments: [] });
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLead || !onUpdateLead) return;
    onUpdateLead(editingLead);
    setEditingLead(null);
  };

  const handleAttachmentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewLead(prev => ({
          ...prev,
          attachments: [...(prev.attachments || []), reader.result as string]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleExistingLeadAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length || !selectedLeadForAttachments || !onUpdateLead) return;
    const files = Array.from(e.target.files);
    
    const newAttachments = await Promise.all(files.map(file => {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
    }));

    const updatedLead = {
      ...selectedLeadForAttachments,
      attachments: [...(selectedLeadForAttachments.attachments || []), ...newAttachments]
    };
    
    setSelectedLeadForAttachments(updatedLead);
    onUpdateLead(updatedLead);
  };

  const handleCardUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    setIsScanning(true);
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(',')[1];
      const data = await extractBusinessCardData(base64);
      if (data) {
        setNewLead(prev => ({
          ...prev,
          clientName: data.clientName || prev.clientName,
          email: data.email || prev.email,
          phone: data.phone || prev.phone,
          address: data.address || prev.address
        }));
        setShowForm(true);
      }
      setIsScanning(false);
    };
    reader.readAsDataURL(file);
  };

  const generateInvite = (lead: Lead) => {
    const baseUrl = window.location.origin + window.location.pathname;
    const link = `${baseUrl}?name=${encodeURIComponent(lead.clientName)}&email=${encodeURIComponent(lead.email)}&address=${encodeURIComponent(lead.address)}`;
    setInviteLink(link);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-bark">Incoming Opportunities</h3>
        <div className="flex space-x-3">
          <input 
            type="file" 
            className="hidden" 
            ref={cardInputRef} 
            accept="image/*" 
            onChange={handleCardUpload} 
          />
          <button 
            disabled={isScanning}
            onClick={() => cardInputRef.current?.click()}
            className="bg-stone-200 text-bark px-4 py-2 rounded-xl font-bold hover:bg-stone-300 transition-colors flex items-center space-x-2"
          >
            {isScanning ? 'Scanning...' : 'Scan Business Card'}
          </button>
          <button 
            onClick={() => setShowForm(true)}
            className="bg-palm text-white px-6 py-2 rounded-xl font-bold hover:bg-gecko transition-colors"
          >
            + New Lead
          </button>
        </div>
      </div>

      {pendingRequests.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6">
          <div className="mb-4">
            <h4 className="text-lg font-black text-amber-900">
              {pendingRequests.length} request{pendingRequests.length === 1 ? '' : 's'} awaiting action
            </h4>
            <p className="text-sm text-amber-700">New estimate requests. Book the site visit, or convert straight to a job.</p>
          </div>
          <div className="space-y-3">
            {pendingRequests.map(lead => (
              <div key={lead.id} className="bg-white rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 border border-amber-100">
                <div>
                  <div className="font-bold text-bark">{lead.clientName}</div>
                  <div className="text-xs text-stone-500">
                    {[lead.phone, lead.email, lead.serviceType].filter(Boolean).join('  ·  ')}
                  </div>
                  {lead.address && <div className="text-xs text-stone-400 mt-0.5">{lead.address}</div>}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => { setEstimateFor(lead); setEstimateForm({ date: new Date().toISOString().slice(0, 10), time: '09:00', durationMins: 60, notes: '' }); }}
                    className="bg-gecko text-white px-4 py-2 rounded-xl font-bold text-sm hover:bg-palm transition"
                  >
                    Schedule Estimate
                  </button>
                  <button
                    onClick={() => setConfirmingLeadId(lead.id)}
                    className="text-palm font-bold text-sm hover:underline"
                  >
                    Create Job
                  </button>
                  <button
                    onClick={() => setLeadToDelete(lead)}
                    className="text-red-500 font-bold text-sm hover:underline"
                    title="Delete this lead"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-bark/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl">
            <h4 className="text-2xl font-bold mb-6 text-palm">Lead Intake</h4>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-bold mb-1">Client Name</label>
                <input 
                  type="text" 
                  value={newLead.clientName}
                  className="w-full p-3 bg-stone-100 rounded-lg border-none focus:ring-2 focus:ring-gecko" 
                  onChange={e => setNewLead({...newLead, clientName: e.target.value})} 
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Email</label>
                <input 
                  type="email" 
                  value={newLead.email}
                  className="w-full p-3 bg-stone-100 rounded-lg border-none focus:ring-2 focus:ring-gecko"
                  onChange={e => setNewLead({...newLead, email: e.target.value})} 
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Address</label>
                <input 
                  type="text" 
                  value={newLead.address}
                  className="w-full p-3 bg-stone-100 rounded-lg border-none focus:ring-2 focus:ring-gecko"
                  onChange={e => setNewLead({...newLead, address: e.target.value})} 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-1">Service Type</label>
                  <select 
                    className="w-full p-3 bg-stone-100 rounded-lg border-none focus:ring-2 focus:ring-gecko"
                    value={newLead.serviceType}
                    onChange={e => setNewLead({...newLead, serviceType: e.target.value as any})}
                  >
                    <option>Pruning</option>
                    <option>Removal</option>
                    <option>Plant Health</option>
                    <option>Emergency</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">Phone</label>
                  <input 
                    type="tel" 
                    value={newLead.phone}
                    className="w-full p-3 bg-stone-100 rounded-lg border-none focus:ring-2 focus:ring-gecko"
                    onChange={e => setNewLead({...newLead, phone: e.target.value})} 
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-bold mb-1">Attachments (Photos/Diagrams)</label>
                <input 
                  type="file" 
                  multiple 
                  accept="image/*"
                  onChange={handleAttachmentUpload}
                  className="w-full p-2 bg-stone-100 rounded-lg border-none text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gecko/10 file:text-gecko hover:file:bg-gecko/20 transition"
                />
                {newLead.attachments && newLead.attachments.length > 0 && (
                  <div className="mt-3 grid grid-cols-4 gap-2">
                    {newLead.attachments.map((src, idx) => (
                      <div key={idx} className="relative group">
                        <img src={src} alt="Attachment" className="h-16 w-full object-cover rounded-lg border border-stone-200" />
                        <button 
                          type="button"
                          onClick={() => setNewLead(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== idx) }))}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowForm(false)} 
                  className="flex-1 py-3 text-stone-500 font-bold hover:bg-stone-100 rounded-lg transition"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="flex-1 py-3 bg-gecko text-white font-bold rounded-lg hover:bg-palm transition"
                >
                  Create Lead
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {inviteLink && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg p-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-bark mb-4">Client Invite Link Generated</h3>
            <p className="text-stone-500 mb-6">Send this link to the customer. It will pre-populate their account creation form with their contact details.</p>
            <div className="bg-stone-50 p-4 rounded-xl border border-stone-200 break-all text-xs font-mono text-palm mb-6">
              {inviteLink}
            </div>
            <div className="flex space-x-3">
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(inviteLink);
                  alert('Copied to clipboard!');
                }}
                className="flex-1 py-3 bg-palm text-white font-bold rounded-xl"
              >
                Copy Link
              </button>
              <button 
                onClick={() => setInviteLink(null)}
                className="flex-1 py-3 bg-stone-100 text-stone-500 font-bold rounded-xl"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmingLeadId && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl text-center">
            <div className="w-16 h-16 bg-gecko/20 text-gecko rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-bark mb-2">Convert to Work Order?</h3>
            <p className="text-stone-500 mb-8 leading-relaxed">
              This will move the lead into the active jobs pipeline and generate a draft estimate.
            </p>
            <div className="flex space-x-3">
              <button 
                onClick={() => setConfirmingLeadId(null)} 
                className="flex-1 py-3 border-2 border-stone-100 text-stone-500 font-bold rounded-xl hover:bg-stone-50 transition"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  onConvert(confirmingLeadId);
                  setConfirmingLeadId(null);
                }} 
                className="flex-1 py-3 bg-palm text-white font-bold rounded-xl hover:bg-bark transition shadow-lg shadow-palm/20"
              >
                Create Work Order
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedLeadForAttachments && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl p-8 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-bark">Attachments for {selectedLeadForAttachments.clientName}</h3>
              <button onClick={() => setSelectedLeadForAttachments(null)} className="text-stone-400 hover:text-stone-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            
            <div className="mb-6">
              <label className="inline-block bg-gecko text-white px-4 py-2 rounded-xl font-bold hover:bg-palm transition-colors cursor-pointer">
                + Upload Photo/Diagram
                <input type="file" className="hidden" multiple accept="image/*" onChange={handleExistingLeadAttachmentUpload} />
              </label>
            </div>

            {selectedLeadForAttachments.attachments && selectedLeadForAttachments.attachments.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-96 overflow-y-auto custom-scrollbar p-2">
                {selectedLeadForAttachments.attachments.map((src, idx) => (
                  <div key={idx} className="relative group rounded-xl overflow-hidden border border-stone-200">
                    <img src={src} alt={`Attachment ${idx + 1}`} className="w-full h-32 object-cover" />
                    <a 
                      href={src} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="absolute inset-0 bg-bark/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <span className="text-white font-bold text-sm">View Full</span>
                    </a>
                    <button 
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!onUpdateLead) return;
                        const updatedLead = {
                          ...selectedLeadForAttachments,
                          attachments: selectedLeadForAttachments.attachments?.filter((_, i) => i !== idx)
                        };
                        setSelectedLeadForAttachments(updatedLead);
                        onUpdateLead(updatedLead);
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 border-2 border-dashed border-stone-200 rounded-xl text-center text-stone-400">
                No attachments or diagrams provided for this lead.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="overflow-hidden bg-white rounded-2xl border border-stone-200">
        <table className="w-full text-left">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-stone-500">Client</th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-stone-500">Address</th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-stone-500">Service</th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-stone-500">Invite</th>
              <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-stone-500">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {leads.map(lead => (
              <tr key={lead.id} className="hover:bg-stone-50 transition-colors">
                <td className="px-6 py-4">
                  <p className="font-bold text-bark">{lead.clientName}</p>
                  <p className="text-xs text-stone-400">{lead.email}</p>
                </td>
                <td className="px-6 py-4 text-sm text-stone-600">{lead.address}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                    lead.serviceType === 'Removal' ? 'bg-red-100 text-red-600' : 
                    lead.serviceType === 'Emergency' ? 'bg-orange-100 text-orange-600' : 'bg-leaf/20 text-palm'
                  }`}>
                    {lead.serviceType}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button 
                    onClick={() => generateInvite(lead)}
                    className="text-gecko font-bold text-xs hover:underline flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    Generate Invite
                  </button>
                </td>
                <td className="px-6 py-4 flex space-x-3 items-center">
                  <button 
                    onClick={() => setSelectedLeadForAttachments(lead)}
                    className="text-stone-500 font-bold text-sm hover:underline flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                    Attachments ({lead.attachments?.length || 0})
                  </button>
                  <button 
                    onClick={() => { setEstimateFor(lead); setEstimateForm({ date: new Date().toISOString().slice(0, 10), time: '09:00', durationMins: 60, notes: '' }); }}
                    className="text-gecko font-bold text-sm hover:underline flex items-center"
                    title="Book the site visit and build the estimate"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    Schedule Estimate
                  </button>
                  <button 
                    onClick={() => setConfirmingLeadId(lead.id)}
                    className="text-palm font-bold text-sm hover:underline"
                  >
                    Create Job
                  </button>                  <button 
                    onClick={() => setEditingLead({ ...lead })}
                    className="text-stone-500 font-bold text-sm hover:underline"
                    title="Edit lead"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => setLeadToDelete(lead)}
                    className="text-red-500 font-bold text-sm hover:underline"
                    title="Delete lead"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Lead Modal */}
      {editingLead && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="bg-palm p-6 text-white">
              <h3 className="text-xl font-bold">Edit Lead</h3>
              <p className="text-palm-100 text-sm opacity-80 italic">Update the opportunity details.</p>
            </div>
            <form onSubmit={handleEditSubmit} className="p-8 space-y-4">
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Client Name</label>
                <input required type="text" value={editingLead.clientName} onChange={e => setEditingLead({ ...editingLead, clientName: e.target.value })}
                  className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Email</label>
                  <input type="email" value={editingLead.email} onChange={e => setEditingLead({ ...editingLead, email: e.target.value })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Phone</label>
                  <input type="text" value={editingLead.phone} onChange={e => setEditingLead({ ...editingLead, phone: e.target.value })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Address</label>
                <input type="text" value={editingLead.address} onChange={e => setEditingLead({ ...editingLead, address: e.target.value })}
                  className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Service Type</label>
                <select value={editingLead.serviceType} onChange={e => setEditingLead({ ...editingLead, serviceType: e.target.value as any })}
                  className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none">
                  {['Removal', 'Pruning', 'Plant Health', 'Emergency', 'Consulting'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex space-x-3 pt-2">
                <button type="button" onClick={() => setEditingLead(null)} className="flex-1 py-3 text-stone-500 font-bold hover:bg-stone-100 rounded-lg transition">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-gecko text-white font-bold rounded-lg hover:bg-palm transition shadow-lg shadow-gecko/20">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Lead Modal */}
      {leadToDelete && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h3 className="text-2xl font-bold text-bark mb-2">Delete this lead?</h3>
            <p className="text-stone-500 mb-8 leading-relaxed">This soft-deletes <span className="font-bold text-bark">{leadToDelete.clientName}</span>. The record stays in the change log for audit and can be restored.</p>
            {deleteError && (
              <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4 text-left">
                {deleteError}
              </p>
            )}
            <div className="flex space-x-3">
              <button onClick={() => { setLeadToDelete(null); setDeleteError(''); }} disabled={isDeleting} className="flex-1 py-3 border-2 border-stone-100 text-stone-500 font-bold rounded-xl hover:bg-stone-50 transition disabled:opacity-50">Cancel</button>
              <button onClick={confirmDeleteLead} disabled={isDeleting} className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition disabled:opacity-60">{isDeleting ? 'Deleting…' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
      {estimateFor && (
        <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full my-8">
            <div className="p-6 border-b border-stone-100 flex items-start justify-between">
              <div>
                <h3 className="text-2xl font-bold text-bark">Schedule Estimate</h3>
                <p className="text-stone-500 text-sm mt-1">Book the site visit. The job moves to <span className="font-bold">Estimating</span> and appears on your calendar.</p>
              </div>
              <button onClick={() => setEstimateFor(null)} className="text-stone-400 hover:text-stone-600 text-2xl leading-none">&times;</button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (onScheduleEstimate && estimateFor) onScheduleEstimate(estimateFor, estimateForm);
                setEstimateFor(null);
              }}
              className="p-6 space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Client Name</label>
                  <input readOnly value={estimateFor.clientName || ''} className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 text-stone-600" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Phone</label>
                  <input readOnly value={estimateFor.phone || ''} className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 text-stone-600" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Email</label>
                  <input readOnly value={estimateFor.email || ''} className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 text-stone-600" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Address</label>
                  <input readOnly value={estimateFor.address || ''} className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 text-stone-600" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Visit Date</label>
                  <input required type="date" value={estimateForm.date} onChange={e => setEstimateForm({ ...estimateForm, date: e.target.value })} className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Time</label>
                  <input required type="time" value={estimateForm.time} onChange={e => setEstimateForm({ ...estimateForm, time: e.target.value })} className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Est. Minutes</label>
                  <input type="number" min="15" step="15" value={estimateForm.durationMins} onChange={e => setEstimateForm({ ...estimateForm, durationMins: Number(e.target.value) })} className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Site Notes</label>
                <textarea rows={3} placeholder="Tree type, access, hazards, what the customer described..." value={estimateForm.notes} onChange={e => setEstimateForm({ ...estimateForm, notes: e.target.value })} className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none resize-none" />
              </div>

              {estimateFor.address && (
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Property</label>
                  <div className="rounded-xl overflow-hidden border border-stone-200">
                    <MapComponent address={estimateFor.address} title={estimateFor.clientName} />
                  </div>
                </div>
              )}

              <div className="flex space-x-3 pt-2">
                <button type="button" onClick={() => setEstimateFor(null)} className="flex-1 py-3 border-2 border-stone-100 text-stone-500 font-bold rounded-xl hover:bg-stone-50 transition">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-gecko text-white font-bold rounded-xl hover:bg-palm transition">Schedule Estimate</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsView;
